import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, Button, Grid, Card, CardContent, Stack,
    Chip, Divider, useTheme, useMediaQuery, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, FormControlLabel, Checkbox, Alert
} from '@mui/material';
import {
    FaLeaf, FaMicrochip, FaShoppingCart, FaArrowLeft
} from 'react-icons/fa';

export default function BuyController() {
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [interestOpen, setInterestOpen] = React.useState(false);
    const [interestSent, setInterestSent] = React.useState(false);
    const [privacyAccepted, setPrivacyAccepted] = React.useState(false);
    const [offersAccepted, setOffersAccepted] = React.useState(false);

    const handleInterestSubmit = (event) => {
        event.preventDefault();
        if (!privacyAccepted) return;
        setInterestSent(true);
    };

    return (
        <Box sx={{ bgcolor: 'background.default', color: 'text.primary', minHeight: '100vh' }}>
            {/* Header */}
            <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 10, backdropFilter: 'blur(10px)', background: 'rgba(18,18,18,0.8)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
                        <FaLeaf size={26} color="#4CAF50" />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>AutoGrow BR</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1}>
                        <Button variant="outlined" startIcon={<FaArrowLeft />} onClick={() => navigate('/')}>
                            Início
                        </Button>
                        <Button variant="contained" startIcon={<FaLeaf />} onClick={() => navigate('/guia')}>
                            Guia de Cultivo
                        </Button>
                    </Stack>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: { xs: 4, sm: 6, md: 9 }, px: { xs: 2, sm: 3 } }}>
                <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto', mb: { xs: 3, md: 5 } }}>
                    <Chip label="AUTOGROW BR SHOP" color="primary" sx={{ mb: 2, fontWeight: 700, letterSpacing: 0.4 }} />
                    <Typography variant={isMobile ? 'h4' : 'h3'} sx={{ fontWeight: 800 }}>
                        Seu cultivo no piloto automático
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mt: 1, fontWeight: 400 }}>
                        Tenha mais controle, praticidade e tranquilidade em cada etapa do seu cultivo.
                    </Typography>
                </Box>

                <Grid container spacing={{ xs: 3, md: 4 }} alignItems="stretch">
                    {/* Card do produto */}
                    <Grid item xs={12} md={5}>
                        <Card sx={{ display: 'flex', flexDirection: 'column', background: 'linear-gradient(160deg, rgba(76,175,80,0.18), rgba(33,150,243,0.12))', border: '1px solid rgba(76,175,80,0.3)', borderRadius: 4, height: '100%' }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', flex: 1, p: { xs: 2, sm: 3 } }}>
                                <Box sx={{ height: { xs: 200, sm: 230, md: 260 }, mx: { xs: -2, sm: -3 }, mt: { xs: -2, sm: -3 }, mb: { xs: 2, sm: 3 }, overflow: 'hidden', borderRadius: { xs: '14px 14px 0 0', sm: '16px 16px 0 0' } }}>
                                    <Box component="img" src="/landing/hero-device.jpg" alt="Controlador AutoGrow BR conectado ao aplicativo" sx={{ width: 1, height: 1, objectFit: 'cover' }} />
                                </Box>
                                <Typography variant="h5" sx={{ fontWeight: 800, textAlign: 'center' }}>Controlador AutoGrow BR</Typography>
                                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1, mb: 2 }}>
                                    <Chip label="Automação" color="primary" size="small" />
                                    <Chip label="Wi-Fi" size="small" />
                                    <Chip label="Tempo real" size="small" />
                                </Stack>
                                <Divider sx={{ my: 2 }} />
                                <Typography variant="h4" sx={{ fontWeight: 800, textAlign: 'center', color: 'primary.main' }}>
                                    Em breve na loja
                                </Typography>
                                <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
                                    Estamos preparando tudo para abrir os pedidos. Cadastre seu interesse e seja avisado.
                                </Typography>
                                <Button fullWidth variant="contained" size="large" onClick={() => { setInterestSent(false); setInterestOpen(true); }} sx={{ mt: 3 }}>
                                    Quero ser avisado
                                </Button>
                                <Button fullWidth variant="outlined" sx={{ mt: 1 }} onClick={() => navigate('/guia')}>
                                    Conheça o Guia de Cultivo
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Próximo passo */}
                    <Grid item xs={12} md={7}>
                        <Box sx={{ mt: { xs: 0, md: 5 }, p: { xs: 2.5, sm: 3.5 }, borderRadius: 3, background: 'rgba(33,150,243,0.08)', border: '1px solid rgba(33,150,243,0.25)' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Pronto para cultivar com mais facilidade?</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Enquanto a loja não abre, conheça o Guia de Cultivo e descubra como o AutoGrow BR pode ajudar.
                            </Typography>
                            <Button variant="contained" startIcon={<FaLeaf />} onClick={() => navigate('/guia')}>
                                Ir para o Guia de Cultivo
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            <Box component="footer" sx={{ py: 4, borderTop: '1px solid rgba(255,255,255,0.06)', textAlign: 'center' }}>
                <Container maxWidth="lg">
                    <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mb: 1 }}>
                        <Box component="img" src="/logo-3d-art-print.png" alt="3D Art Print" sx={{ width: 32, height: 32, objectFit: 'contain' }} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>3D Art Print</Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                        © 2026 3D Art Print. Todos os direitos reservados.
                    </Typography>
                </Container>
            </Box>

            <Dialog open={interestOpen} onClose={() => setInterestOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Avise-me quando a loja abrir</DialogTitle>
                {interestSent ? (
                    <DialogContent>
                        <Alert severity="success" sx={{ mt: 1 }}>
                            Interesse registrado! Avisaremos quando o controlador estiver disponível.
                        </Alert>
                    </DialogContent>
                ) : (
                    <Box component="form" onSubmit={handleInterestSubmit}>
                        <DialogContent>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Preencha seus dados para receber o aviso de disponibilidade.
                            </Typography>
                            <TextField fullWidth required label="Nome completo" margin="dense" name="fullName" />
                            <TextField fullWidth required type="email" label="E-mail" margin="dense" name="email" />
                            <TextField fullWidth required label="Telefone" margin="dense" name="phone" inputProps={{ inputMode: 'tel' }} />
                            <FormControlLabel
                                control={<Checkbox checked={privacyAccepted} onChange={(e) => setPrivacyAccepted(e.target.checked)} required />}
                                label="Li e concordo com os termos de privacidade."
                                sx={{ mt: 1, alignItems: 'flex-start' }}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={offersAccepted} onChange={(e) => setOffersAccepted(e.target.checked)} />}
                                label="Aceito receber ofertas e novidades por e-mail e WhatsApp."
                                sx={{ alignItems: 'flex-start' }}
                            />
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 2 }}>
                            <Button onClick={() => setInterestOpen(false)}>Cancelar</Button>
                            <Button type="submit" variant="contained" disabled={!privacyAccepted}>Enviar interesse</Button>
                        </DialogActions>
                    </Box>
                )}
                {interestSent && <DialogActions><Button onClick={() => setInterestOpen(false)}>Fechar</Button></DialogActions>}
            </Dialog>
        </Box>
    );
}
